import React, { useContext, useRef, useState, useMemo, useEffect } from "react";
import { Form } from "react-bootstrap";
import { gql, useQuery } from "@apollo/client";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../starek.css";
import StarekContext from "../../context/StarekContext";
import ConfirmModal from "../../components/modals/ConfirmModal";
import PartOperationCard from "./PartOperationCard";
import {
    SidebarView,
    SidebarContextProvider,
    SidebarContext,
    useSyncedField,
} from "../../components/Sidebar";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_IMAGE_SIZE_MB = 2;

const GET_OPERATIONS_FOR_PART = gql`
    query GetOperationsForPart($where: OperationWhereInput!) {
        operations(where: $where) {
            id
            name
            is_productive
        }
    }
`;

const GET_DEVICES_WITH_CAPABILITIES = gql`
    query GetDevicesForPartStages($where: ShadowDeviceWhereInput!) {
        shadowDevices(where: $where) {
            id
            name
            installed_at_zones {
                name
            }
            capabilities {
                operation_id
            }
        }
    }
`;

const GET_JOB_SCHEDULES_FOR_PART = gql`
    query GetJobSchedulesForPart($partId: Int!) {
        jobSchedules(where: { part_id: { equals: $partId } }) {
            id
            name
            end_time
        }
    }
`;

function PartImageUpload() {
    const [imageDataUrl, setImageDataUrl] = useSyncedField("image_data_url", "");
    const [, setImageFile] = useSyncedField("image_file", null);
    const { entity } = useContext(SidebarContext);
    const inputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            alert("Only JPEG, PNG, WebP, or GIF images are allowed.");
            return;
        }

        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            alert(`Image must be smaller than ${MAX_IMAGE_SIZE_MB}MB.`);
            return;
        }

        setImageFile(file);

        const reader = new FileReader();
        reader.onload = (ev) => setImageDataUrl(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleRemove = () => {
        setImageDataUrl("");
        setImageFile(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const existingUrl = entity?.image_asset?.url;
    const previewSrc = imageDataUrl || existingUrl;

    return (
        <Form.Group className="mb-3" controlId="part_image">
            {/* <Form.Label className="d-block text-center">
                Image <span className="text-muted" style={{ fontSize: "0.8rem" }}>(simulation)</span>
            </Form.Label> */}

            <div className="d-flex flex-column align-items-center gap-2">
                {previewSrc ? (
                    <>
                        <img
                            src={previewSrc}
                            alt="Part preview"
                            style={{
                                width: 120,
                                height: 120,
                                objectFit: "cover",
                                borderRadius: "50%",
                                border: "2px solid #dee2e6",
                            }}
                        />
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => inputRef.current?.click()}
                            >
                                <i className="fa fa-pencil me-1" /> Change
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={handleRemove}
                            >
                                <i className="fa fa-trash me-1" /> Remove
                            </button>
                        </div>
                    </>
                ) : (
                    <div
                        className="d-flex flex-column align-items-center justify-content-center border"
                        style={{
                            width: 120,
                            height: 120,
                            borderRadius: "50%",
                            cursor: "pointer",
                            background: "#f8f9fa",
                            color: "#6c757d",
                        }}
                        onClick={() => inputRef.current?.click()}
                        role="button"
                        aria-label="Upload part image"
                    >
                        <i className="fa fa-camera fa-2x mb-1" />
                        <small>Upload</small>
                    </div>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(",")}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </Form.Group>
    );
}

function PartBasicDetails() {
    const [name, setName] = useSyncedField("name", "");
    const [description, setDescription] = useSyncedField("description", "");

    return (
        <>
            <PartImageUpload />
            <Form.Group className="mb-3" controlId="part_name">
                <Form.Label>
                    Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter here"
                    maxLength={191}
                    name="part_name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Form.Group>
            <Form.Group className="mb-3" controlId="part_description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Optional, up to 191 characters"
                    maxLength={191}
                    name="part_description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </Form.Group>
        </>
    );
}

// Wrapper so each sequence row can be dragged.
function SortablePartOperationCard(props) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <PartOperationCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
        </div>
    );
}

function PartOperations({ plantId }) {
    const starekContext = useContext(StarekContext);
    const { entity } = useContext(SidebarContext);

    // Ordered list of rows. Position in this array IS sequence_no.
    const [rows, setRows] = useSyncedField("partOperations", []);
    const [deletedIds, setDeletedIds] = useSyncedField(
        "deletedPartStageIds",
        [],
    );

    const entityIdRef = useRef(null);
    const rowCounterRef = useRef(0);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const { data: operationsData, loading: loadingOperations } = useQuery(
        GET_OPERATIONS_FOR_PART,
        {
            client: starekContext.engineClient,
            skip: !plantId,
            // Operations and device links change from other screens; never use the cache.
            fetchPolicy: "network-only",
            variables: {
                where: {
                    plant_id: { equals: plantId },
                    is_active: { equals: true },
                },
            },
        },
    );

    const { data: devicesData, loading: loadingDevices } = useQuery(
        GET_DEVICES_WITH_CAPABILITIES,
        {
            client: starekContext.engineClient,
            skip: !plantId,
            fetchPolicy: "network-only",
            variables: {
                where: {
                    plant_id: { equals: plantId },
                    is_active: { equals: true },
                },
            },
        },
    );

    // Used only to warn before removing an operation from a part that is already scheduled.
    const { data: jobSchedulesData } = useQuery(GET_JOB_SCHEDULES_FOR_PART, {
        client: starekContext.engineClient,
        skip: !entity?.id,
        variables: { partId: Number(entity?.id) },
        fetchPolicy: "network-only",
    });

    const activeJobSchedules = useMemo(() => {
        const now = Date.now();
        return (jobSchedulesData?.jobSchedules || []).filter(
            (js) => new Date(js.end_time).getTime() >= now,
        );
    }, [jobSchedulesData?.jobSchedules]);

    const operations = useMemo(
        () => operationsData?.operations || [],
        [operationsData?.operations],
    );

    const operationOptions = useMemo(
        () =>
            operations.map((o) => ({
                value: Number(o.id),
                label: o.name,
            })),
        [operations],
    );

    const devices = useMemo(
        () => devicesData?.shadowDevices || [],
        [devicesData?.shadowDevices],
    );

    const deviceLabel = (deviceId) => {
        const device = devices.find((d) => d.id === deviceId);
        if (!device) return deviceId;
        const zone = device.installed_at_zones?.[0]?.name;
        return `${device.id}${zone ? ` (${zone})` : ""}`;
    };

    const devicesForOperation = (operationId) =>
        devices.filter((d) =>
            (d.capabilities || []).some(
                (c) => Number(c.operation_id) === Number(operationId),
            ),
        );

    const operationsById = useMemo(() => {
        const map = {};
        operations.forEach((o) => {
            map[Number(o.id)] = o;
        });
        // Keep operations that are linked but no longer active resolvable by name.
        (entity?.part_stages || []).forEach((ps) => {
            if (ps.operation && !map[Number(ps.operation_id)]) {
                map[Number(ps.operation_id)] = ps.operation;
            }
        });
        return map;
    }, [operations, entity?.part_stages]);

    const nextRowId = () => {
        rowCounterRef.current += 1;
        return `row-${rowCounterRef.current}`;
    };

    useEffect(() => {
        const currentEntityId = entity?.id || null;
        if (entityIdRef.current === currentEntityId && currentEntityId !== null) {
            return;
        }
        entityIdRef.current = currentEntityId;

        const stages = Array.isArray(entity?.part_stages)
            ? [...entity.part_stages].sort(
                  (a, b) => (a.sequence_no || 0) - (b.sequence_no || 0),
              )
            : [];

        setRows(
            stages.map((ps) => {
                rowCounterRef.current += 1;
                return {
                    rowId: `row-${rowCounterRef.current}`,
                    id: ps.id,
                    operation_id: Number(ps.operation_id),
                    device_ids: [...(ps.allowed_devices || [])]
                        .sort((a, b) => (a.position || 0) - (b.position || 0))
                        .map((ad) => ad.device_id),
                    standard_duration: ps.standard_duration ?? "",
                };
            }),
        );
        setDeletedIds([]);
        // Deliberately keyed on the entity id alone — re-running on every render would
        // discard the rows the user is editing.
    }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const addRow = () => {
        setRows([
            ...rows,
            {
                rowId: nextRowId(),
                operation_id: null,
                device_ids: [],
                standard_duration: "",
            },
        ]);
    };

    const updateRow = (rowId, patch) => {
        setRows(
            rows.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)),
        );
    };

    const applyRemove = (rowId) => {
        const target = rows.find((r) => r.rowId === rowId);
        setRows(rows.filter((r) => r.rowId !== rowId));
        if (target?.id) {
            setDeletedIds(
                deletedIds.includes(target.id)
                    ? deletedIds
                    : [...deletedIds, target.id],
            );
        }
    };

    // AC4 — removing an operation from a Part that an active Job Schedule references
    // is allowed, but only after an explicit confirmation.
    const removeRow = (rowId) => {
        const target = rows.find((r) => r.rowId === rowId);
        const operationName =
            operationsById[target?.operation_id]?.name || "this operation";

        if (activeJobSchedules.length === 0) {
            applyRemove(rowId);
            return;
        }

        setConfirmCfg({
            open: true,
            title: `Remove ${operationName} from this part?`,
            content: (
                <div className="text-dark" style={{ whiteSpace: "pre-wrap" }}>
                    {`This part is referenced by ${activeJobSchedules.length} active job schedule(s):\n\n${activeJobSchedules
                        .slice(0, 5)
                        .map((js) => `• ${js.name}`)
                        .join("\n")}${
                        activeJobSchedules.length > 5
                            ? `\n• and ${activeJobSchedules.length - 5} more`
                            : ""
                    }\n\nRemoving this operation changes what the part is made of. The change applies when you save.`}
                </div>
            ),
            confirmText: "Remove",
            cancelText: "Cancel",
            type: "warning",
            onConfirm: () => {
                applyRemove(rowId);
                setConfirmCfg({ open: false });
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = rows.findIndex((r) => r.rowId === active.id);
        const newIndex = rows.findIndex((r) => r.rowId === over.id);
        setRows(arrayMove(rows, oldIndex, newIndex));
    };

    const move = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= rows.length) return;
        setRows(arrayMove(rows, index, target));
    };

    return (
        <Form className="d-flex flex-column h-100">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div>Route</div>
                <div
                    className="text-primary cursor-pointer"
                    onClick={addRow}
                    style={{ cursor: "pointer" }}
                >
                    <i className="fa fa-plus me-2"></i>
                    Add step
                </div>
            </div>

            {loadingOperations && (
                <div className="text-muted small mb-2">
                    Loading operations...
                </div>
            )}

            {!loadingOperations && operationOptions.length === 0 && (
                <div className="text-muted small mb-2">
                    No operations defined for this plant yet. Add them under
                    Setup &rarr; Operations.
                </div>
            )}

            {rows.length === 0 ? (
                <div className="border rounded p-3 text-muted small">
                    No route yet. This part has no build sequence — add steps
                    in the order they are performed.
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={rows.map((r) => r.rowId)}
                        strategy={verticalListSortingStrategy}
                    >
                        {rows.map((row, index) => (
                            <div key={row.rowId} className="d-flex align-items-start">
                                <div className="d-flex flex-column me-2 pt-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link p-0 text-secondary"
                                        title="Move up"
                                        disabled={index === 0}
                                        onClick={() => move(index, -1)}
                                    >
                                        <i className="fa fa-chevron-up" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link p-0 text-secondary"
                                        title="Move down"
                                        disabled={index === rows.length - 1}
                                        onClick={() => move(index, 1)}
                                    >
                                        <i className="fa fa-chevron-down" />
                                    </button>
                                </div>
                                <div className="flex-grow-1">
                                    <SortablePartOperationCard
                                        id={row.rowId}
                                        position={index + 1}
                                        row={row}
                                        operationOptions={operationOptions}
                                        operationsById={operationsById}
                                        devicesForOperation={devicesForOperation(
                                            row.operation_id,
                                        )}
                                        deviceLabel={deviceLabel}
                                        loadingDevices={loadingDevices}
                                        onChange={updateRow}
                                        onRemove={removeRow}
                                    />
                                </div>
                            </div>
                        ))}
                    </SortableContext>
                </DndContext>
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </Form>
    );
}

/**
 * A part is only useful once its Route says which operations build it, in order. That
 * lives on this same sidebar's second tab, so the first step re-opens the part there
 * rather than describing where to click.
 */
const partNextSteps = ({ data, entity, isNew, saved }) => {
    const name = data.name?.trim() || entity?.name || "Part";
    const partId = saved?.id ?? entity?.id ?? null;
    const routeIsEmpty = !(data.partOperations || []).length;

    const steps = [];

    if (partId) {
        steps.push({
            label: routeIsEmpty ? "Build this part's route" : "Review this part's route",
            why: routeIsEmpty
                ? "Add the operations in order — a part with no route cannot be scheduled."
                : "Each step carries its allowed machines, a default machine and a duration.",
            path: ["Setup", "Parts", name, "Route"],
            to: `/parts/${partId}?tab=route`,
        });
    }

    steps.push({
        label: "Schedule a run for this part",
        why: "The run copies the route as it stands, one machine per step.",
        path: ["Plan", "Job Schedules", "Add"],
        to: "/job-schedules",
    });

    return {
        heading: `"${name}" ${isNew ? "created" : "updated"}`,
        subheading:
            isNew && routeIsEmpty
                ? "It has no route yet, so there is nothing to schedule."
                : null,
        allowAddAnother: isNew,
        steps,
    };
};

function PartSidebar({
    show,
    item,
    plantId,
    onDiscard,
    onSave,
    onDelete,
    onToggleActive,
    initialPage = "basic_details",
}) {
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            if (!emittedData.name?.trim()) {
                starekContext.showError("Part name is required");
                throw new Error("Part name is required");
            }

            const rows = Array.isArray(emittedData.partOperations)
                ? emittedData.partOperations
                : [];

            if (rows.some((r) => !r.operation_id)) {
                starekContext.showError(
                    "Every step in the route needs an operation selected",
                );
                throw new Error("Operation is required on every sequence row");
            }

            const payload = {
                id: entity?.id,
                name: emittedData.name.trim(),
                description: emittedData.description || "",
                plant_id: plantId,
                image_file: emittedData.image_file || null,
                // sequence_no is derived from row order, renumbered contiguously from 1
                partOperations: rows.map((r, index) => ({
                    id: r.id,
                    operation_id: Number(r.operation_id),
                    sequence_no: index + 1,
                    // Ordered: first is the preferred machine. Empty means any capable one.
                    device_ids: r.device_ids || [],
                    // Seconds; null when the user left it blank.
                    standard_duration:
                        r.standard_duration === "" ||
                        r.standard_duration === null ||
                        r.standard_duration === undefined
                            ? null
                            : Number(r.standard_duration),
                })),
                deletedPartStageIds: emittedData.deletedPartStageIds || [],
            };

            return await onSave(payload);
        } catch (error) {
            console.error("Error saving part:", error);
            starekContext.showError(
                error?.message || "Failed to save part. Please try again.",
            );
            throw error;
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={item?.id ? async (entity) => await onDelete(entity) : null}
            onDiscard={async () => await onDiscard()}
            onToggleActive={onToggleActive}
            initialPage={initialPage}
            nextSteps={partNextSteps}
        >
            <SidebarView entityName="Part" entityCode="part">
                <SidebarView.Page
                    key={item?.id || "new"}
                    name="basic_details"
                    title="Basic Details"
                >
                    <PartBasicDetails />
                </SidebarView.Page>

                <SidebarView.Page name="operations" title="Route">
                    <PartOperations plantId={plantId} />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
}

export default PartSidebar;
